/* ==========================================================================
   Macaje — affinity.js
   On-device personalization for the upsell surfaces. No apps, no third-party
   calls, no data leaves the browser: signals live in localStorage and the only
   network requests are to the shop's own storefront endpoints.

   Signals recorded:
     • product views   — count + last-seen per product, written on every PDP load
     • cart removals   — a line that leaves the bag is the strongest "almost
                         bought" signal there is; found by diffing each
                         cart:updated event against the last known cart
     • cart adds       — clears a product's removal record (they came back)

   Surfaces personalized:
     • cart drawer "Why not add" rail — server renders a collection fallback,
       this swaps in behavior-ranked picks (see personalizeRail)
     • PDP "You May Also Like"       — Shopify's related products, re-ranked so
       pieces the visitor already looked at lead (see mcj:recs listener)
     • PDP "Complete the look" rows  — curated pairs reordered viewed-first

   Rendering stays in Liquid: ranked ids go through the search endpoint with
   sections/macaje-picks.liquid as the renderer — the same trick
   <recently-viewed> already uses — so cards, prices and availability are always
   server-truth and this file never templates product HTML.
   ========================================================================== */

(() => {
  'use strict';

  // The Theme Editor re-renders sections constantly and Ziad's editing sessions
  // are not shopping intent — stay completely inert there.
  if (window.Shopify && window.Shopify.designMode) return;

  const KEY = 'macaje:affinity';
  const DAY = 864e5;
  const now = () => Date.now();
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------- storage */

  const read = () => {
    try {
      const d = JSON.parse(localStorage.getItem(KEY) || '{}');
      return d && typeof d === 'object' ? d : {};
    } catch { return {}; }
  };

  const get = () => {
    const d = read();
    d.views = d.views || {};
    d.removes = d.removes || {};
    d.cart = d.cart || null;
    return d;
  };

  // Keep the record bounded: newest-first caps, so a heavy browser never grows
  // the entry past a few KB and JSON.parse stays instant.
  const cap = (obj, n) => {
    const keys = Object.keys(obj);
    if (keys.length <= n) return obj;
    keys.sort((a, b) => (obj[b].t || 0) - (obj[a].t || 0));
    const out = {};
    keys.slice(0, n).forEach((k) => { out[k] = obj[k]; });
    return out;
  };

  const save = (d) => {
    d.views = cap(d.views, 40);
    d.removes = cap(d.removes, 20);
    try { localStorage.setItem(KEY, JSON.stringify(d)); } catch { /* private mode */ }
  };

  // Honor Shopify's consent API when a consent app has declined analytics;
  // absent the API (no consent app installed), first-party on-device
  // personalization proceeds.
  const allowed = () => {
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() !== false;
      }
    } catch { /* treat as allowed */ }
    return true;
  };

  /* ------------------------------------------------------------- signals */

  // A finish is the one style axis that must never mismatch in a jewelry
  // pairing: gold with gold, silver with silver. "mixed" (two-tone) pieces
  // pair with either.
  const finishOf = (s) => {
    s = String(s || '').toLowerCase();
    if (/two[- ]tone|mixed[- ]tone|toi et moi/.test(s)) return 'mixed';
    if (/silver/.test(s)) return /gold/.test(s) ? 'mixed' : 'silver';
    if (/rose[- ]gold/.test(s)) return 'rose';
    return 'gold';
  };

  // PDP context, embedded by main-product.liquid.
  let pdp = null;
  const pdpEl = document.querySelector('[data-mcj-product]');
  if (pdpEl) {
    try { pdp = JSON.parse(pdpEl.textContent); } catch { pdp = null; }
  }
  const pdpFinish = pdp ? finishOf([pdp.material, pdp.title, (pdp.tags || []).join(' ')].join(' ')) : null;
  const pdpType = pdp ? String(pdp.type || '').toLowerCase() : null;

  if (pdp && allowed()) {
    const d = get();
    const v = d.views[pdp.id] || { n: 0 };
    v.n = Math.min(v.n + 1, 20);
    v.t = now();
    v.h = pdp.handle;
    v.ty = pdpType;
    v.p = pdp.price;
    v.f = pdpFinish;
    d.views[pdp.id] = v;
    save(d);
  }

  const snapCart = (cart) => ({
    t: now(),
    items: (cart.items || []).map((i) => ({
      pid: i.product_id,
      q: i.quantity,
      h: i.handle,
      ti: i.product_title || i.title,
      ty: String(i.product_type || '').toLowerCase(),
      p: i.price
    }))
  });

  // Diff each cart update against the last known cart. Only a single product
  // going down counts as a removal — several lines vanishing at once is a
  // checkout or a cart clear, not a change of heart about one piece.
  const onCartUpdated = (cart) => {
    const d = get();
    const prev = d.cart;
    if (prev && allowed()) {
      const before = {};
      prev.items.forEach((i) => { before[i.pid] = (before[i.pid] || 0) + i.q; });
      const after = {};
      (cart.items || []).forEach((i) => { after[i.product_id] = (after[i.product_id] || 0) + i.quantity; });

      const gone = Object.keys(before).filter((pid) => (after[pid] || 0) < before[pid]);
      const added = Object.keys(after).filter((pid) => (before[pid] || 0) < after[pid]);

      if (gone.length === 1) {
        const pid = gone[0];
        const was = prev.items.find((i) => String(i.pid) === String(pid));
        d.removes[pid] = {
          t: now(),
          h: was && was.h,
          ti: was && was.ti,
          ty: was && was.ty,
          p: was && was.p,
          f: finishOf(was && was.ti)
        };
      }
      added.forEach((pid) => { delete d.removes[pid]; });
    }
    d.cart = snapCart(cart);
    save(d);
  };

  document.addEventListener('cart:updated', (e) => {
    onCartUpdated(e.detail.cart);
    lastCart = e.detail.cart;
    scheduleRail();
  });

  /* ------------------------------------------------------------- scoring */

  const decay = (t, halfDays) => {
    const age = Math.max(0, now() - (t || 0));
    return Math.pow(0.5, age / (halfDays * DAY));
  };

  // Behavior first, then style. Weights are ordered by strength of intent:
  // a removal beats repeated views beats "related to the bag"; style bonuses
  // (matching finish, a category the bag lacks, an easy add-on price) break
  // ties rather than drive the ranking.
  const score = (c, d, ctx) => {
    let s = 0;
    const rm = d.removes[c.id];
    if (rm) s += 3.5 * decay(rm.t, 7);
    const v = d.views[c.id];
    if (v) s += 1.2 * Math.log2(1 + (v.n || 1)) * decay(v.t, 4);
    if (c.rel) s += 1.0 - 0.03 * (c.relPos || 0);
    if (ctx.finish && c.finish && (c.finish === ctx.finish || c.finish === 'mixed' || ctx.finish === 'mixed')) s += 0.5;
    if (c.type && ctx.types && !ctx.types.has(c.type)) s += 0.45;
    if (ctx.median && c.price && c.price <= ctx.median * 1.25) s += 0.3;
    return s;
  };

  const relatedFor = async (pid, limit) => {
    try {
      const res = await fetch(`${root()}recommendations/products.json?product_id=${pid}&limit=${limit}&intent=related`);
      if (!res.ok) return [];
      return (await res.json()).products || [];
    } catch { return []; }
  };

  const pickForCart = async (cart, exclude) => {
    const d = get();
    const items = cart.items || [];
    const inCart = new Set(items.map((i) => String(i.product_id)));
    const types = new Set(items.map((i) => String(i.product_type || '').toLowerCase()).filter(Boolean));

    const prices = items.map((i) => i.price).sort((a, b) => a - b);
    const median = prices.length ? prices[Math.floor(prices.length / 2)] : 0;

    const fCounts = {};
    items.forEach((i) => {
      const f = finishOf(i.product_title || i.title);
      fCounts[f] = (fCounts[f] || 0) + 1;
    });
    const finish = Object.keys(fCounts).sort((a, b) => fCounts[b] - fCounts[a])[0] || null;

    const ctx = { types, median, finish };

    const cands = new Map();
    const push = (id, c) => {
      id = String(id);
      if (inCart.has(id) || exclude.has(id)) return;
      cands.set(id, Object.assign(cands.get(id) || { id }, c));
    };

    Object.entries(d.removes).forEach(([id, r]) => push(id, { type: r.ty, price: r.p, finish: r.f }));
    Object.entries(d.views).forEach(([id, v]) => push(id, { type: v.ty, price: v.p, finish: v.f }));

    // Related-to-the-bag, seeded from the two most recent lines. This is what
    // personalizes the rail even for a first-time visitor with no history.
    const seeds = items.slice(0, 2).map((i) => i.product_id);
    const rel = await Promise.all(seeds.map((pid) => relatedFor(pid, 8)));
    rel.flat().forEach((p, i) => {
      if (p.available === false) return;
      push(p.id, {
        type: String(p.type || p.product_type || '').toLowerCase(),
        price: p.price,
        finish: finishOf((p.tags || []).join(' ') + ' ' + p.title),
        rel: true,
        relPos: i
      });
    });

    return [...cands.values()]
      .map((c) => ({ c, s: score(c, d, ctx) }))
      .filter((x) => x.s > 0.2)
      .sort((a, b) => b.s - a.s)
      .slice(0, 8)
      .map((x) => x.c);
  };

  /* ----------------------------------------------------------- rendering */

  // Liquid renders the cards: ranked ids go through the search endpoint with
  // macaje-picks as the section renderer, then the cards come back in rank
  // order (search's own order is relevance, so reorder by id here).
  const renderPicks = async (ids) => {
    const q = ids.map((id) => `id:${id}`).join(' OR ');
    const res = await fetch(`${root()}search?type=product&q=${encodeURIComponent(q)}&section_id=macaje-picks`);
    if (!res.ok) return null;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const byId = new Map();
    doc.querySelectorAll('[data-mcj-card]').forEach((el) => byId.set(String(el.dataset.mcjCard), el));
    return ids.map((id) => byId.get(String(id))).filter(Boolean);
  };

  let lastCart = null;
  let runToken = 0;

  const personalizeRail = async (cart) => {
    const rail = document.querySelector('[data-mcj-rail]');
    if (!rail || !cart || !cart.item_count) return;

    const token = ++runToken;
    const exclude = new Set((rail.dataset.mcjExclude || '').split(',').filter(Boolean));
    if (pdp) exclude.add(String(pdp.id));

    const picks = await pickForCart(cart, exclude);
    if (token !== runToken) return;
    // Under three picks would read sparse next to the server rail — keep the
    // fallback rather than showing a thin "personalized" row.
    if (picks.length < 3) return;

    const cards = await renderPicks(picks.map((p) => p.id));
    if (token !== runToken || !cards || cards.length < 3) return;

    const track = rail.querySelector('[data-carousel-track]');
    if (!track) return;
    track.replaceChildren(...cards);
    track.scrollLeft = 0;

    const head = rail.querySelector('.upsell-head');
    if (head && rail.dataset.mcjHeading) head.textContent = rail.dataset.mcjHeading;
    rail.dataset.mcjDone = 'true';
  };

  let railTimer = null;
  const scheduleRail = () => {
    clearTimeout(railTimer);
    railTimer = setTimeout(runRail, 80);
  };

  const runRail = async () => {
    let cart = lastCart;
    if (!cart) {
      try {
        const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
        cart = await res.json();
      } catch { return; }
      lastCart = cart;
      // First sight of the cart this page: baseline for removal diffing.
      const d = get();
      if (!d.cart) { d.cart = snapCart(cart); save(d); }
    }
    personalizeRail(cart);
  };

  // The drawer replaces .drawer__main wholesale after every cart mutation
  // (render() in theme.js), which discards any personalized rail — watch for
  // the fresh server rail landing and re-personalize it.
  const drawer = document.getElementById('CartDrawer');
  if (drawer) {
    new MutationObserver(() => {
      if (drawer.querySelector('[data-mcj-rail]:not([data-mcj-done])')) scheduleRail();
    }).observe(drawer, { childList: true, subtree: true });
    document.addEventListener('drawer:open', (e) => {
      if (e.target && e.target.id === 'CartDrawer') scheduleRail();
    });
  }

  /* -------------------------------------------- PDP re-ranks (no fetches) */

  // "You May Also Like": once product-recommendations swaps its cards in, lead
  // with the pieces this visitor already spent time on. Stable sort — with no
  // history the order is exactly Shopify's.
  document.addEventListener('mcj:recs', (e) => {
    const el = e.detail && e.detail.el;
    if (!el) return;
    const track = el.querySelector('[data-carousel-track]');
    if (!track) return;
    const d = get();
    const value = (li) => {
      const id = li.dataset.mcjId;
      let s = 0;
      if (d.removes[id]) s += 3;
      if (d.views[id]) s += 1 + Math.min(1, (d.views[id].n || 1) / 5);
      const f = li.dataset.mcjFinish;
      const ty = String(li.dataset.mcjType || '').toLowerCase();
      if (pdpFinish && f && (f === pdpFinish || f === 'mixed' || pdpFinish === 'mixed')) s += 0.4;
      if (pdpType && ty && ty !== pdpType) s += 0.3;
      return s;
    };
    // appendChild on a node that is ALREADY in the document disconnects and
    // reconnects it, which re-runs the connectedCallback of every custom
    // element inside — <quick-add> among them. That is how a single tap on an
    // upsell used to send two /cart/add.js requests and land a line item at
    // quantity 2 (theme.js's bindOnce() is the fix at source; this stops the
    // move happening at all when there is nothing to reorder).
    //
    // Ranking with no visitor history returns the cards in exactly the order
    // they arrived, so the common case is now a no-op instead of a full
    // re-append of every card.
    const ordered = [...track.children]
      .map((li, i) => ({ li, i, s: value(li) }))
      .sort((a, b) => b.s - a.s || a.i - b.i);
    if (ordered.every((x, i) => x.i === i)) return;
    ordered.forEach((x) => track.appendChild(x.li));
  });

  // "Complete the look" rows: curated pairs, but the one the visitor already
  // viewed goes first.
  const ctl = document.querySelector('complete-look [data-mcj-pairs]');
  if (ctl && ctl.children.length > 1) {
    const d = get();
    // Same reason as the rail above: only move rows when the order actually
    // changes, so an unpersonalized visit never re-connects a card.
    const ordered = [...ctl.children]
      .map((row, i) => ({ row, i, s: d.views[row.dataset.mcjId] ? 1 : 0 }))
      .sort((a, b) => b.s - a.s || a.i - b.i);
    if (!ordered.every((x, i) => x.i === i)) ordered.forEach((x) => ctl.appendChild(x.row));
  }

  // Console handle for QA on the preview: MacajeAffinity.data() shows what the
  // device has recorded; picks() dry-runs the cart ranking.
  window.MacajeAffinity = {
    data: get,
    picks: async () => {
      const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
      return pickForCart(await res.json(), new Set());
    }
  };
})();
